import { CheckIcon, SkipForwardIcon, UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SlotItem } from "../utils";
import { formatDueTime } from "../utils";
import { useSlotMutations } from "../hooks/useSlotMutations";

export function RecoverSection({ items, lead }: { items: SlotItem[]; lead?: boolean }) {
  if (items.length === 0) return null;

  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2 px-1">
        <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          {lead ? "Wrap up the day" : "Needs attention"}
        </h2>
        <span className="text-[11px] text-muted-foreground/70">still doable</span>
      </div>
      <div className="rounded-2xl border border-border bg-muted/30 divide-y divide-border/60 overflow-hidden">
        {items.map((item) => (
          <RecoverRow key={item.slot.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function RecoverRow({ item }: { item: SlotItem }) {
  const { complete, completeRecovery, skip, isLoading } = useSlotMutations(
    item.goalId,
    item.slot.id,
  );

  const recoveryAction = item.slot.recoveryAction;

  return (
    <div className={`px-4 py-3 ${isLoading ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug truncate">{item.slot.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {item.goalName} ·{" "}
            <span className="text-chart-3 tabular-nums">
              was due {formatDueTime(item.slot.dueTimeLocal)}
            </span>
          </p>
          {item.notifyCircleName && (
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground/80 mt-0.5 truncate">
              <UsersIcon className="w-3 h-3 shrink-0" />
              shared with {item.notifyCircleName}
            </p>
          )}
        </div>
        {!recoveryAction && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              disabled={isLoading}
              onClick={() => complete.mutate()}
            >
              <CheckIcon strokeWidth={3} />
              Do it now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isLoading}
              onClick={() => skip.mutate()}
              className="text-muted-foreground"
            >
              <SkipForwardIcon />
              Skip today
            </Button>
          </div>
        )}
      </div>

      {recoveryAction && (
        <div className="flex flex-wrap items-center gap-2 mt-2.5">
          <Button
            variant="secondary"
            size="sm"
            disabled={isLoading}
            onClick={() => completeRecovery.mutate()}
            className="max-w-full"
          >
            <CheckIcon strokeWidth={3} />
            <span className="truncate">Do recovery: {recoveryAction}</span>
          </Button>
          <button
            onClick={() => complete.mutate()}
            disabled={isLoading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-lg hover:bg-muted disabled:opacity-40"
          >
            Do it now
          </button>
          <button
            onClick={() => skip.mutate()}
            disabled={isLoading}
            className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors px-2 py-1.5 rounded-lg hover:bg-muted disabled:opacity-40"
          >
            Skip today
          </button>
        </div>
      )}
    </div>
  );
}
